import { inject, Injectable } from '@angular/core';
import { ObjectState, StatefullValue } from '@meddev/fe-shared';
import FileSaver from 'file-saver';
import {
    BehaviorSubject,
    catchError,
    combineLatest,
    filter,
    first,
    map,
    Observable,
    of,
    shareReplay,
    switchMap,
    throttleTime,
    throwError,
} from 'rxjs';
import { ErrorCodes } from '../../../../../../shared/data/src/lib/enum/error-codes.enum';
import { ClaimType } from '../../../core/enum/claims/claim-type.enum';
import { AttachmentModel } from '../../../core/interface/attachment-model';
import { CombinedCustomerClaimModel } from '../../../core/interface/claims/customer-claims-model';
import { BaseReimbursementRequestBodyModel } from '../../../core/interface/claims/reimbursement-body-model';
import { ReportClaimBodyModel } from '../../../core/interface/claims/report-claim-body-model';
import { CofusMedicalDocumentationBodyModel } from '../../../core/interface/documentation/medical-documentation-body-model';
import { ClaimManagerService } from '../../domain/manager/claim-manager.service';
import { InsurancePoliciesManagerService } from '../../domain/manager/insurance-policies-manager.service';

@Injectable({
    providedIn: 'root',
})
export class CustomerClaimsAppService {
    private readonly ReportClaimsRefreshDebounceTime = 250;
    private claimsManagerService = inject(ClaimManagerService);
    private insurancePoliciesManager = inject(InsurancePoliciesManagerService);
    private claimsRefreshSubject = new BehaviorSubject<void>(undefined);
    private customerClaimsDataStream$ = this.createCustomerClaimsDataStream();

    public sendReportClaim(
        request: CofusMedicalDocumentationBodyModel,
        files?: File[],
        customerComment?: string,
    ): Observable<StatefullValue<unknown, ErrorCodes>> {
        return of(null).pipe(
            switchMap(() => {
                return this.claimsManagerService.setMedicalDocumentation(request, files);
            }),
            switchMap(medicalDocumentationRes => {
                const claimData: ReportClaimBodyModel = {
                    medicalDocumentationId: medicalDocumentationRes?.id,
                    customerComment: customerComment,
                };
                return this.claimsManagerService.sendReportClaim(claimData);
            }),
            map(() => {
                return { value: null as unknown as ReportClaimBodyModel, state: ObjectState.VALID };
            }),
            catchError(() => {
                return of({ value: null as unknown as ReportClaimBodyModel, state: ObjectState.ERROR });
            }),
        );
    }

    public sendReimbursementClaim(request: BaseReimbursementRequestBodyModel, files?: File[]): Observable<StatefullValue<unknown, ErrorCodes>> {
        return of(null).pipe(
            switchMap(() => {
                return this.insurancePoliciesManager.getPolicies().pipe(
                    filter(result => result.state === ObjectState.VALID || result.state === ObjectState.ERROR),
                    switchMap(result => {
                        if (result.state === ObjectState.VALID) {
                            // TODO - remove fetching policies when policyNumber will be selected added to a UI dropdown
                            const policyNumber = result.value?.[0].insurancePolicyNo;
                            return this.claimsManagerService.sendReimbursementClaim(request, policyNumber, files);
                        } else {
                            return throwError(() => new Error());
                        }
                    }),
                );
            }),
            map(() => {
                return { value: null as unknown as string, state: ObjectState.VALID };
            }),
            catchError(() => {
                return of({ value: null as unknown as string, state: ObjectState.ERROR });
            }),
        );
    }

    public refreshCustomerReportClaims(): void {
        this.claimsRefreshSubject.next();
    }

    public getCustomerCombinedClaims(): Observable<StatefullValue<CombinedCustomerClaimModel[], ErrorCodes>> {
        return this.customerClaimsDataStream$.pipe(
            map(statefulValue => {
                if (statefulValue.state === ObjectState.VALID) {
                    const sortedClaims = [...(statefulValue.value || [])].sort((a, b) => {
                        const dateA = a.submittedAt ? a.submittedAt.valueOf() : 0;
                        const dateB = b.submittedAt ? b.submittedAt.valueOf() : 0;
                        return dateB - dateA;
                    });

                    return {
                        ...statefulValue,
                        value: sortedClaims,
                    };
                }
                return statefulValue;
            }),
        );
    }

    public cancelCustomerClaim(claimUuid: string): Observable<unknown> {
        return this.customerClaimsDataStream$.pipe(
            first(),
            map(data => data.value),
            map(claims => claims.find(claim => claim.uuid === claimUuid)),
            switchMap(claim => {
                if (!claim) {
                    return throwError(() => null);
                }
                if (!claim?.isCancelable) {
                    return throwError(() => null);
                }
                if (claim.claimType === ClaimType.Reimbursement) {
                    return this.claimsManagerService.cancelReimbursementClaim(claim.claimId);
                } else if (claim.claimType === ClaimType.ReportClaim) {
                    return this.claimsManagerService.cancelReportClaim(claim.claimId);
                }
                return throwError(() => null);
            }),
        );
    }

    public downloadClaimDocument(claimUuid: string, attachment: AttachmentModel): void {
        this.customerClaimsDataStream$
            .pipe(
                first(),
                map(data => data.value),
                map(claims => claims.find(claim => claim.uuid === claimUuid)),
                switchMap(claim => {
                    if (!claim) {
                        return throwError(() => null);
                    }
                    if (claim.claimType === ClaimType.Reimbursement) {
                        return this.claimsManagerService.getReimbursementDocument(claim.uuid, attachment.documentId);
                    } else if (claim.claimType === ClaimType.ReportClaim) {
                        return this.claimsManagerService.getReportClaimDocument(claim.uuid, attachment.documentId);
                    }
                    return throwError(() => null);
                }),
                map(documentModel => {
                    const file = new File([documentModel.data], attachment.name, { type: documentModel.mediaType });
                    return file;
                }),
            )
            .subscribe(file => {
                FileSaver.saveAs(file, attachment.name);
            });
    }

    private createCustomerClaimsDataStream(): Observable<StatefullValue<CombinedCustomerClaimModel[], ErrorCodes>> {
        return this.claimsRefreshSubject.pipe(
            throttleTime(this.ReportClaimsRefreshDebounceTime),
            switchMap(() =>
                combineLatest([
                    this.claimsManagerService.getCustomerReportClaims(),
                    this.claimsManagerService.getCustomerReimbursementtClaims(),
                ]).pipe(
                    map(([reportClaims, reimbursementClaims]) => {
                        const allClaims = [...(reportClaims.value || []), ...(reimbursementClaims.value || [])];

                        const states = [reportClaims.state, reimbursementClaims.state];
                        const finalState = this.determineCombinedState(states);

                        return {
                            value: finalState === ObjectState.VALID ? allClaims : [],
                            state: finalState,
                        };
                    }),
                    catchError(() => of({ value: [], state: ObjectState.ERROR } as StatefullValue<CombinedCustomerClaimModel[], ErrorCodes>)),
                ),
            ),
            shareReplay({ bufferSize: 1, refCount: true }),
        );
    }

    private determineCombinedState(states: ObjectState[]): ObjectState {
        if (states.includes(ObjectState.ERROR)) {
            return ObjectState.ERROR;
        }
        if (states.includes(ObjectState.LOADING)) {
            return ObjectState.LOADING;
        }
        return ObjectState.VALID;
    }
}
